import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { resolveApiBaseList } from '../utils/apiBase'; // reutiliza lista de hosts

// Ventana y recesos (deben coincidir con el backend)
const WINDOW_START = '07:00:00';
const WINDOW_END   = '15:00:00';
const RECESOS = [
  ['10:00:00','10:30:00'],
  ['12:30:00','12:45:00'],
];

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

/* ----------------- Utilidades de tiempo ----------------- */
const toMinutes = (hhmmss) => {
  const [h,m,s] = hhmmss.split(':').map(Number);
  return h*60 + m + (s ? s/60 : 0);
};
const toHHMM = (mins) => {
  const h = Math.floor(mins/60);
  const m = Math.round(mins%60);
  const pad = (x) => String(x).padStart(2,'0');
  return `${pad(h)}:${pad(m)}`;
};
const hhmmToHHMMSS = (hhmm) => hhmm ? `${hhmm}:00` : '';
const overlaps = (aIni, aFin, bIni, bFin) => (aIni < bFin) && (aFin > bIni);
const cmpTime = (a,b) => a.localeCompare(b);

/**
 * Dada la ventana [07:00–15:00], los recesos y las horas ocupadas del backend,
 * genera segmentos contiguos que cubren todo el día con estado:
 *  - "RECESO" si cruza un receso
 *  - "No disponible" si cruza algún ocupado
 *  - "Disponible" en otro caso
 * Si un segmento está ocupado, incluye el nombre de la Materia encontrada.
 */
function buildSegments(ocupadas = []) {
  const cuts = new Set([WINDOW_START, WINDOW_END]);
  // cortes por recesos
  RECESOS.forEach(([i,f]) => { cuts.add(i); cuts.add(f); });
  // cortes por ocupadas
  ocupadas.forEach(o => { cuts.add(o.HoraInicio); cuts.add(o.HoraFin); });

  const sorted = Array.from(cuts).sort(cmpTime);
  const segs = [];
  for (let i=0; i<sorted.length-1; i++) {
    const ini = sorted[i], fin = sorted[i+1];
    if (cmpTime(ini, fin) >= 0) continue;
    // etiqueta
    let label = 'Disponible', materia = null;
    // receso manda
    if (RECESOS.some(([ri,rf]) => overlaps(ini, fin, ri, rf))) {
      label = 'RECESO';
    } else {
      const choque = ocupadas.find(o => overlaps(ini, fin, o.HoraInicio, o.HoraFin));
      if (choque) { label = 'No disponible'; materia = choque.Materia || null; }
    }
    segs.push({
      ini, fin,
      label,
      materia,
      // para pintar bonito
      display: `${ini.slice(0,5)} - ${fin.slice(0,5)}`
    });
  }
  return segs;
}

const RegistrarHorarios = () => {
  // form state
  const [nextId, setNextId] = useState('');
  const [grupoId, setGrupoId] = useState('');
  const [materiaId, setMateriaId] = useState('');
  const [diaSemana, setDiaSemana] = useState('');
  const [horaInicio, setHoraInicio] = useState(''); // HH:MM:SS
  const [horaFinal, setHoraFinal]   = useState(''); // HH:MM:SS
  const [aula, setAula] = useState('');

  // combos
  const [grupos, setGrupos] = useState([]);
  const [materias, setMaterias] = useState([]);

  // disponibilidad dinámica del día
  const [ocupadas, setOcupadas] = useState([]); // /horas-ocupadas
  const [segments, setSegments] = useState([]); // parrilla generada

  // nombre docente (de tu sesión)
  const docenteNombre = useMemo(
    () => localStorage.getItem('displayName') || 'Usuario',
    []
  );

  const navigate = useNavigate();
  const location = useLocation();

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // conservar mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    if (rol) navigate(`/${rol}/inicio`, { replace: true });
    else navigate('/inicio', { replace: true });
  };

  /* --------- Cargar combos --------- */
  useEffect(() => {
    fetchJsonWithFallback('/horarios/siguiente-id')
      .then(d => setNextId(d?.nextId ?? ''))
      .catch((error) => {
        console.error('No fue posible obtener el siguiente ID de horario:', error);
        setNextId('');
      });
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    fetchJsonWithFallback('/horarios/grupos')
      .then((data) => setGrupos(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('No fue posible cargar los grupos:', error);
        setGrupos([]);
      });
  }, [fetchJsonWithFallback]);

  // Al cambiar grupo: set aula, limpiar materia/horas, y cargar materias
  useEffect(() => {
    if (!grupoId) {
      setMaterias([]);
      setAula('');
      setMateriaId('');
      setHoraInicio('');
      setHoraFinal('');
      return;
    }
    setAula(String(grupoId)); // aula = IdGrupo
    setMateriaId('');
    setHoraInicio('');
    setHoraFinal('');
    fetchJsonWithFallback(`/horarios/materias?grupoId=${grupoId}`)
      .then((data) => setMaterias(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('No fue posible cargar las materias del grupo:', error);
        setMaterias([]);
      });
  }, [fetchJsonWithFallback, grupoId]);

  // Al cambiar día: limpiar materia/horas
  useEffect(() => {
    if (!diaSemana) return;
    setHoraInicio('');
    setHoraFinal('');
  }, [diaSemana]);

  // Cargar ocupadas del día → construir parrilla dinámica
  useEffect(() => {
    if (!grupoId || !diaSemana) {
      setOcupadas([]);
      setSegments([]);
      return;
    }
    fetchJsonWithFallback(`/horarios/horas-ocupadas?grupoId=${grupoId}&dia=${encodeURIComponent(diaSemana)}`)
      .then((rows) => {
        setOcupadas(rows || []);
        setSegments(buildSegments(rows || []));
      })
      .catch((error) => {
        console.error('No fue posible cargar la disponibilidad del día:', error);
        setOcupadas([]);
        setSegments(buildSegments([]));
      });
  }, [fetchJsonWithFallback, grupoId, diaSemana]);

  /* --------- Handlers de hora --------- */
  const onChangeTimeInicio = (val) => setHoraInicio(hhmmToHHMMSS(val));
  const onChangeTimeFin    = (val) => setHoraFinal(hhmmToHHMMSS(val));

  /* --------- Submit --------- */
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!grupoId)   return Swal.fire('Atención', 'Selecciona un grupo.', 'warning');
    if (!materiaId) return Swal.fire('Atención', 'Selecciona una materia.', 'warning');
    if (!diaSemana) return Swal.fire('Atención', 'Selecciona un día.', 'warning');

    // Validaciones de horas personalizadas
    if (!horaInicio || !horaFinal) {
      return Swal.fire('Atención', 'Selecciona hora de inicio y fin.', 'warning');
    }
    if (horaInicio >= horaFinal) {
      return Swal.fire('Atención', 'La hora de inicio debe ser menor que la hora fin.', 'warning');
    }
    if (horaInicio < WINDOW_START || horaFinal > WINDOW_END) {
      return Swal.fire('Atención', `Las horas deben estar entre ${WINDOW_START.slice(0,5)} y ${WINDOW_END.slice(0,5)}.`, 'warning');
    }
    if (RECESOS.some(([ri,rf]) => overlaps(horaInicio, horaFinal, ri, rf))) {
      return Swal.fire('Atención', 'No se pueden registrar clases que crucen un RECESO.', 'warning');
    }

    try {
      const data = await fetchJsonWithFallback('/horarios/registrar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idGrupo: Number(grupoId),
          idMateria: Number(materiaId),
          diaSemana,
          horaInicio,
          horaFin: horaFinal,
          docenteNombre
        })
      });

      if (data?.ok === false) {
        return Swal.fire('Error', data?.mensaje || 'Error al registrar el horario', 'error');
      }

      const mat = materias.find(m => String(m.IdMateria) === String(materiaId));
      const materiaNombre = mat?.Nombre || `Materia ${materiaId}`;
      const rangoLabel = `${horaInicio.slice(0,5)} - ${horaFinal.slice(0,5)}`;

      await Swal.fire({
        icon: 'success',
        title: 'Horario registrado',
        html: `
          <div class="text-start">
            <div><b>Docente:</b> ${docenteNombre}</div>
            <div><b>Grupo:</b> ${grupoId}</div>
            <div><b>Materia:</b> ${materiaNombre}</div>
            <div><b>Día:</b> ${diaSemana}</div>
            <div><b>Hora:</b> ${rangoLabel}</div>
            ${data?.aulaSugerida ? `<div><b>Aula:</b> ${data.aulaSugerida}</div>` : ''}
          </div>
        `,
        showConfirmButton: false,
        timer: 2200
      });

      // refrescar ocupadas/parrilla tras registrar
      const rows = await fetchJsonWithFallback(`/horarios/horas-ocupadas?grupoId=${grupoId}&dia=${encodeURIComponent(diaSemana)}`);
      setOcupadas(rows || []);
      setSegments(buildSegments(rows || []));

      // limpiar selección de hora (opcional)
      setHoraInicio('');
      setHoraFinal('');

    } catch (err) {
      Swal.fire('Error', `Error al registrar el horario: ${err.message}`, 'error');
    }
  };

  return (
    <div className="container py-4 px-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Registrar Horarios</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Form */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3" id="formularioHorarios" onSubmit={onSubmit}>
          <div className="col-md-4">
            <label className="form-label">ID Horario</label>
                <input
                  type="text"
                  className="form-control"
                  value={nextId || 'Auto'}
                  disabled
                />
          </div>

          <div className="col-md-4">
            <label className="form-label">Grupo</label>
            <select className="form-select" value={grupoId} onChange={e => setGrupoId(e.target.value)}>
              <option value="" disabled>Selecciona Grupo</option>
              {grupos.map(g => (
                <option key={g.IdGrupo} value={g.IdGrupo}>
                  {`Grupo ${g.IdGrupo} — Grado ${g.Grado}`}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Materia a Impartir</label>
            <select
              className="form-select"
              value={materiaId}
              onChange={e => setMateriaId(e.target.value)}
              disabled={!grupoId}
            >
              <option value="" disabled>Selecciona Materia</option>
              {materias.map(m => (
                <option key={m.IdMateria} value={m.IdMateria}>{m.Nombre}</option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Día de la Semana</label>
            <select className="form-select" value={diaSemana} onChange={e => setDiaSemana(e.target.value)}>
              <option value="" disabled>Selecciona Día</option>
              {DIAS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Horas personalizadas */}
          <div className="col-md-4">
            <label className="form-label">Rango de hora (personalizado)</label>
            <div className="d-flex gap-2">
              <div className="flex-fill">
                <label className="form-label">Inicio</label>
                <input
                  type="time"
                  className="form-control"
                  min="07:00"
                  max="15:00"
                  step="300"
                  value={horaInicio ? horaInicio.slice(0,5) : ''}
                  onChange={e => onChangeTimeInicio(e.target.value)}
                  disabled={!diaSemana}
                />
              </div>
              <div className="flex-fill">
                <label className="form-label">Fin</label>
                <input
                  type="time"
                  className="form-control"
                  min="07:00"
                  max="15:00"
                  step="300"
                  value={horaFinal ? horaFinal.slice(0,5) : ''}
                  onChange={e => onChangeTimeFin(e.target.value)}
                  disabled={!diaSemana}
                />
              </div>
            </div>
            <div className="form-text">Ventana permitida: 07:00–15:00. (No cruzar recesos)</div>
          </div>

          <div className="col-md-2">
            <label className="form-label">Aula</label>
            <input type="text" className="form-control" value={aula} onChange={e => setAula(e.target.value)} readOnly />
            <div className="form-text">Se autollenará con el número del grupo</div>
          </div>

          <div className="d-flex justify-content-center gap-3 mt-4">
            <button type="submit" className="btn btn-success px-4">Registrar</button>
            <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>Salir</button>
          </div>
        </form>
      </div>

      {/* Parrilla dinámica del día */}
      <div className="mt-4">
        <h6 className="mb-3">Disponibilidad — {diaSemana || 'Selecciona un día'}</h6>
        <table className="table table-bordered table-striped text-center">
          <thead className="table-dark">
            <tr>
              <th>Rango</th>
              <th>Estado</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {!diaSemana ? (
              <tr><td colSpan="3" className="table-light">—</td></tr>
            ) : (
              segments.map((s, idx) => (
                <tr key={`${s.ini}-${s.fin}-${idx}`}>
                  <td>{s.display}</td>
                  {s.label === 'RECESO' ? (
                    <>
                      <td className="table-secondary fw-bold">RECESO</td>
                      <td className="table-secondary">—</td>
                    </>
                  ) : s.label === 'No disponible' ? (
                    <>
                      <td className="table-danger">No disponible</td>
                      <td className="table-danger">{s.materia || ''}</td>
                    </>
                  ) : (
                    <>
                      <td className="table-success">Disponible</td>
                      <td className="table-success">—</td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarHorarios;
